type SchemaLike = {
  name?: string;
  type?: string;
  [key: string]: unknown;
};

export function resolveSchemaNamespace(explicitNamespace?: string): string {
  return (
    explicitNamespace ??
    process.env.NEXT_PUBLIC_SANITY_SCHEMA_NAMESPACE ??
    process.env.SANITY_STUDIO_SCHEMA_NAMESPACE ??
    process.env.SANITY_SCHEMA_NAMESPACE ??
    ''
  ).trim();
}

export function namespaceType(typeName: string, explicitNamespace?: string): string {
  const namespace = resolveSchemaNamespace(explicitNamespace);
  if (!namespace || !typeName) return typeName;
  return typeName.startsWith(`${namespace}.`) ? typeName : `${namespace}.${typeName}`;
}

export function namespaceDocumentId(documentId: string, explicitNamespace?: string): string {
  const namespace = resolveSchemaNamespace(explicitNamespace);
  if (!namespace || !documentId) return documentId;
  return documentId.startsWith(`${namespace}.`) ? documentId : `${namespace}.${documentId}`;
}

export function namespacePagesList<T extends { type: string }>(pagesList: T[], explicitNamespace?: string): T[] {
  const namespace = resolveSchemaNamespace(explicitNamespace);
  if (!namespace) return pagesList;
  return pagesList.map((item) => ({ ...item, type: namespaceType(item.type, namespace) }));
}

export function namespaceSchemaTypes<T extends SchemaLike>(schemaTypes: T[], explicitNamespace?: string): T[] {
  const namespace = resolveSchemaNamespace(explicitNamespace);
  if (!namespace) return schemaTypes;

  const schemaTypeNames = new Set(
    schemaTypes.map((schemaType) => schemaType.name).filter((name): name is string => typeof name === 'string'),
  );

  return schemaTypes.map((schemaType) => namespaceSchemaDefinition(schemaType, schemaTypeNames, namespace)) as T[];
}

function namespaceSchemaDefinition<T>(value: T, schemaTypeNames: Set<string>, namespace: string, isRoot = true): T {
  if (Array.isArray(value)) {
    return value.map((item) => namespaceSchemaDefinition(item, schemaTypeNames, namespace, false)) as T;
  }

  if (!isRecord(value)) {
    return value;
  }

  const namespacedEntries = Object.entries(value).map(([key, entryValue]) => {
    if (key === 'type' && typeof entryValue === 'string' && schemaTypeNames.has(entryValue)) {
      return [key, namespaceType(entryValue, namespace)];
    }

    return [key, namespaceSchemaDefinition(entryValue, schemaTypeNames, namespace, false)];
  });

  const nextValue = Object.fromEntries(namespacedEntries) as T & SchemaLike;

  if (isRoot && typeof value.name === 'string') {
    nextValue.name = namespaceType(value.name, namespace);
  }

  return nextValue;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
